import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CloudSun, Zap, CloudRain, Cloud, Snowflake, Wind, ThermometerSun, ChevronUp, ChevronDown, Sprout } from 'lucide-react';
import { TrustBadge } from '@/components/TrustBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useWeatherPredictions() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/harvests/weather-predictions`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData({ available: false, predictions: [] }));
  }, []);
  return data;
}

export const badgeStyles = (status) => ({
  on_track: { dot: 'bg-[#6B9F4A]', pill: 'bg-[#6B9F4A]/10 text-[#1A4331]' },
  ahead: { dot: 'bg-[#B4D330]', pill: 'bg-[#B4D330]/20 text-[#3d5c14]' },
  behind: { dot: 'bg-[#E2725B]', pill: 'bg-[#E2725B]/10 text-[#E2725B]' },
  stressed: { dot: 'bg-[#C66B4A]', pill: 'bg-[#C66B4A]/15 text-[#C66B4A]' },
}[status] || { dot: 'bg-[#8EAA97]', pill: 'bg-[#8EAA97]/10 text-[#5C665C]' });

const FACTOR_ICONS = {
  frost: Snowflake, snow: Snowflake, heavy_rain: CloudRain,
  cold_wind: Wind, hot_wind: ThermometerSun, cloudy: Cloud,
};

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });

const deltaText = (delta) => {
  if (Math.abs(delta) <= 1) return { text: 'right on schedule', cls: 'text-[#5C665C]' };
  return delta > 0
    ? { text: `${delta} days later than normal`, cls: 'text-[#E2725B]' }
    : { text: `${Math.abs(delta)} days earlier than normal`, cls: 'text-[#3d5c14]' };
};

export const WeatherBadge = ({ badge, testId }) => {
  const s = badgeStyles(badge.status);
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${s.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>{badge.label}
    </span>
  );
};

export const WeatherForecastCard = ({ report, showIndicators = false }) => {
  const [whyOpen, setWhyOpen] = useState(false);
  if (!report) return null;
  const d = deltaText(report.delta_days);
  const chips = report.factors.filter(f => f.key !== 'pace');

  return (
    <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10 shadow-sm" data-testid={`weather-forecast-${report.planting_id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-white rounded-2xl flex items-center justify-center border border-[#1A4331]/10">
            <CloudSun className="w-5 h-5 text-[#1A4331]" />
          </div>
          <div>
            <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#7A6B57]">Weather-driven forecast</p>
            <p className="font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {report.crop_name} <span className="font-normal text-sm text-[#5C665C]">sown {fmtDate(report.sowing_date)}</span>
            </p>
          </div>
        </div>
        <WeatherBadge badge={report.badge} testId={`weather-badge-${report.planting_id}`} />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-[#1A4331]/5">
          <p className="text-[10px] font-bold tracking-wider uppercase text-[#7A6B57]">Predicted harvest</p>
          <p className="text-3xl font-bold text-[#1A4331] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={`predicted-date-${report.planting_id}`}>
            {report.ready ? 'Ready now' : fmtDate(report.predicted_date)}
          </p>
          <p className={`text-xs font-semibold mt-1 ${d.cls}`}>{d.text}</p>
        </div>
        <div className="bg-[#F0F2EB] rounded-2xl p-4">
          <p className="text-[10px] font-bold tracking-wider uppercase text-[#7A6B57]">Normal for this crop</p>
          <p className="text-3xl font-bold text-[#5C665C] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{fmtDate(report.normal_date)}</p>
          <p className="text-xs text-[#7A6B57] mt-1">calendar estimate</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-1.5">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A4331]">
            <Zap className="w-3.5 h-3.5 text-[#C9A85B]" /> Heat units (GDD)
          </p>
          <p className="text-xs text-[#5C665C]" data-testid={`gdd-progress-${report.planting_id}`}>
            {report.gdd.accumulated} / {report.gdd.target} · {report.gdd.pct}%
          </p>
        </div>
        <div className="h-2 bg-[#E5EBDD] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#6B9F4A] to-[#C9A85B] transition-all duration-500" style={{ width: `${report.gdd.pct}%` }}></div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {chips.map(f => {
            const Icon = FACTOR_ICONS[f.key] || Cloud;
            return (
              <span key={f.key} className="inline-flex items-center gap-1.5 bg-white border border-[#1A4331]/10 text-[#5C665C] text-[11px] font-medium px-2.5 py-1 rounded-full" data-testid={`factor-${f.key}-${report.planting_id}`}>
                <Icon className="w-3 h-3" /> {f.label}
                {f.effect && !['faster', 'slower'].includes(f.effect) && <span className="text-[#7A6B57]">· {f.effect}</span>}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => setWhyOpen(!whyOpen)} data-testid={`why-date-toggle-${report.planting_id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A4331] hover:text-[#255C44]">
          Why this date? {whyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
          report.confidence === 'medium-high' ? 'bg-[#6B9F4A]/10 text-[#3d5c14]' : 'bg-[#C9A85B]/15 text-[#7A6B57]'
        }`} data-testid={`forecast-confidence-${report.planting_id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${report.confidence === 'medium-high' ? 'bg-[#B4D330]' : 'bg-[#C9A85B]'}`}></span>
          {report.confidence === 'medium-high' ? 'Medium-high confidence' : 'Medium confidence'}
        </span>
      </div>
      {whyOpen && (
        <p className="mt-2 text-xs text-[#5C665C] leading-relaxed bg-white/60 rounded-xl p-3" data-testid={`why-date-text-${report.planting_id}`}>
          {report.why}
        </p>
      )}

      {showIndicators && report.harvest_indicators?.length > 0 && report.readiness !== 'growing' && (
        <div className="mt-4 pt-4 border-t border-[#1A4331]/10">
          <p className="text-xs font-semibold text-[#2D241E] mb-2">Look for:</p>
          <ul className="text-xs text-[#5C665C] space-y-1">
            {report.harvest_indicators.slice(0, 3).map((indicator, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-[#1A4331] mt-0.5">✓</span>
                <span>{indicator}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const WeatherEstimateLine = ({ report }) => {
  if (!report) return null;
  const delta = report.delta_days;
  const compact = Math.abs(delta) <= 1 ? { text: 'on schedule', cls: 'text-[#5C665C]' }
    : delta > 0 ? { text: `${delta}d late`, cls: 'text-[#E2725B]' }
    : { text: `${Math.abs(delta)}d early`, cls: 'text-[#3d5c14]' };
  return (
    <div className="bg-white/70 rounded-xl px-3 py-2 mt-3" data-testid={`weather-line-${report.planting_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sprout className="w-3.5 h-3.5 text-[#6B9F4A] flex-shrink-0" />
          <span className="text-xs font-semibold text-[#1A4331] truncate">
            {report.ready ? 'Ready to harvest' : `Est. ${fmtDate(report.predicted_date)}`}
          </span>
          <span className={`text-[11px] font-medium ${compact.cls}`}>{compact.text}</span>
        </div>
        <WeatherBadge badge={report.badge} testId={`weather-line-badge-${report.planting_id}`} />
      </div>
      <div className="mt-1.5">
        <TrustBadge confidence={report.confidence} why={report.why}
          basedOn={(report.factors || []).map(f => f.label)}
          testId={`weather-trust-${report.planting_id}`} />
      </div>
    </div>
  );
};
